<template>
  <v-card flat>
    <v-toolbar color="secondary lighten-1">
      <v-toolbar-title class="subtitle-2">
        <v-icon :color="typeIconColor">
          {{ typeIcon }}
        </v-icon>
        <span class="pl-2">{{pluralizedType}} settings</span>
      </v-toolbar-title>
      <v-spacer></v-spacer> 
      <span class="fid-compact-form">advanced search</span>
      <v-checkbox 
        dense
        hide-details 
        class="fid-compact-form"
        v-model="useElastic"
        :disabled="advancedDisabled"
      ></v-checkbox>  
    </v-toolbar>
    <v-card flat v-if="useElastic">
      <v-card-text>
          Advanced search will 
          use the checked fields below to search for matching {{pluralizedType}}. 
          You can change search field priority by unchecking or modifying the weight.
          Results will be ordered by relevance.
          To revert to simple search untick the box above right.
      </v-card-text>
      <v-card  flat v-for="(f,i) in includeFieldList" :key="i">
        <v-card-text class="mt-0 pt-0" >
          <v-data-table
            :mobile-breakpoint="NaN"
            must-sort
            :headers="headers"
            :items="f.fields"
            dense
            hide-default-footer
            no-data-text="no searching information available - try reset below"
          >
            <template v-slot:header.text="{ headers }">
              <v-icon :color="getTypeIconColor(f.type)">
                {{ getTypeIcon(f.type) }} {{ headers }}
              </v-icon>
              <span  class="pl-1 subtitle-1">{{uncamel(f.type)}}</span>
            </template>
            <template v-slot:item.weight="{ item }">
              <v-text-field
                class="fid-compact-form pt-0 mt-0"
                dense
                v-model="item.weight"
                @change="itemChanged(item)"
                :rules="weightRules"
                hide-details
              ></v-text-field>
          
            </template>

            <template v-slot:item.include="{ item }">
  
              <v-checkbox 
                dense
                hide-details 
                v-model="item.include" @change="itemChanged(item)"
                class="fid-compact-form mt-0 pt-0 mb-0 pb-0"
              ></v-checkbox>

            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
      <v-card-actions>
        <v-btn @click="reset" color="accent">Reset</v-btn>
      </v-card-actions>
    </v-card>
    <v-card flat v-else>
      <v-card-text>
        Simple search will look in less fields than advanced search when looking for {{pluralizedType}} 
        and results will be alphabetically ordered. 
        <p v-if="!advancedDisabled">For advanced search tick the box above.</p>
        <p v-else>Advanced search is not yet available when searching for {{pluralizedType}}.</p>
      </v-card-text>
      <v-card-text>
        <simple-search :item-type="itemType"></simple-search>
      </v-card-text>
    </v-card>
  </v-card>
</template>
<script>
// control the includes for elastic search (and regular search too)
import { getTypeDefinition } from '@/shared/searcherfields'
import { Controls, ElDefaults } from '@/gql'
import { getIcon, getIconColor, getStateElDefaults, settings } from '@/js/store'
import { SearchUtils } from '@/js'
import { elDefaultsMutate } from '@/js/apollo'
import Simplesearch from '@/components/Simplesearch'
export default {
  components: {
    "simple-search": Simplesearch
  },
  data: () => ({
    weightRules: [
      v=>!isNaN(parseFloat(v)) || 'A number >0 and <5',
      v=>(parseFloat(v)>0 && parseFloat(v)<5) || 'Weight should be > 0 and < 5 and number like 1.2 or 0.8',
    ],
    Controls: null,
    ElDefaults: null,
    headers: [{
      value:'text',
      text: 'Search field',
      width:'70%',
      sortable: false,
      align: 'start'
    } , {
      value:'weight',
      text: 'Weight',
      sortable: false,
      align: 'start'
    } , {
      value:'include',
      text: 'Include',
      sortable: false,
      align: 'start'
    }]
  }),
  methods: {
    reset () {
      return this.elMutate (this.elStateDefaults.DefaultIncludes) 
    },
    itemChanged(item) {
      // find the changed item
      let nobs = this.elDefaults.slice()
      const nob = {
        key: item.key,
        weight: parseFloat(item.weight),
        __typename: 'ElDefaultInclude'
      }
      const changed = nobs.find(f=>f.key === item.key)
      if(item.include && !changed) {
        nobs.push(nob)
      } else if (item.include && changed) {
        changed.weight = parseFloat(item.weight)
      } else if (!item.include && changed) {
        nobs = nobs.filter(f=>f.key!==item.key)
      }
      return this.elMutate(nobs)
    },
    elMutate (nobs) {
      const wob = {
        __typename: 'ElDefaults',
        Defaults: [{
          __typename: 'ElDefault',
          itemType: this.itemType,
          DefaultIncludes:nobs
        }].concat(this.elOthers),
        ElUses: this.elUses
      };
      return elDefaultsMutate (wob)
    },
    uncamel (text) {
      return SearchUtils.unCamel({ pluralize: true, text })
    },
    getTypeIcon (type) {
      return getIcon(type)
    },
    getTypeIconColor (type) {
      return getIconColor(type)
    }
  },
  computed: {
    advancedDisabled () {
      return settings.elastic.allowedUses.indexOf(this.itemType) === -1
    },
    elUses () {
      return this.ElDefaults && this.ElDefaults.ElUses
    },
    useElastic: {
      get () {
        return (this.elUses || []).find(g=>g===this.itemType)
      },
      set (val) {
        const uses = val ? 
          this.elUses.concat([this.itemType]) : 
          this.elUses.filter(f=>f!==this.itemType)
    
        elDefaultsMutate ({
          __typename: 'ElDefaults',
          ...this.ElDefaults,
          ElUses: uses
        })
      }

    },
    pluralizedType () {
      return this.uncamel(this.itemType) 
    },
    includeFieldList () {
      // need to consolidate fields that have the same type
      // in the spec they are seperate because they have different elastic rules
      const cons = this.makeFieldList && this.makeFieldList.reduce((p,c)=>{
        if(!p[c.type]) {
          p[c.type] = {
            ...c,
            fields: []
          }
        }
        Array.prototype.push.apply(p[c.type].fields,c.fields)
        return p
      },{})
      return Object.keys(cons).map(f=>cons[f])
    },
    makeFieldList () {
      // first get the includes
      if(!this.elDefaults || !this.fieldList) return null
      // enhance the fieldlist with this info, set up whether to include and the weight
      return this.fieldList.map(f=>{
        const fields = f.fields.map(g=>{
          const t = this.elDefaults.find(h=>h.key === g.key);
          return {
            ...g,
            include: Boolean(t),
            weight: t && t.weight ? t.weight : g.weight
          }
        })
        return {
          ...f,
          fields
        }
      })
    },
    elOthers () {
      if(!this.ElDefaults) return null
      return this.ElDefaults.Defaults.filter(f=>f.itemType !== this.itemType)
    },
    elThese () {
      if(!this.ElDefaults) return null
      return this.ElDefaults.Defaults.filter(f=>f.itemType === this.itemType)
    },
    elStateDefaults () {
      const sd = getStateElDefaults()
      return sd && sd.Defaults.find(f=>f.itemType === this.itemType)
    },
    elDefaults () {
      if(!this.ElDefaults) return null
      const da = this.elThese
      if(!da || !da.length){
        console.error('missing eldefaults for', this.itemType)
        return null
      }
      const df = da[0].DefaultIncludes
      if(!df) {
        console.error('no default includes for', this.itemType, da)
      }
      return df
    },
    typeIcon () {
      return this.getTypeIcon(this.itemType)
    },
    typeIconColor () {
      return this.getTypeIconColor(this.itemType)
    },
    typeDefinition () {
      return this.itemType ? getTypeDefinition({type: this.itemType}) : null
    },
    fieldList () {
      return this.typeDefinition && this.typeDefinition.searchFieldList
    },
    itemType () {
      return this.Controls && this.Controls.selectedType
    },
  },
  apollo: {
    Controls: {
      query: Controls
    },
    ElDefaults: {
      query: ElDefaults
    }
  }
}
</script>